import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Solution() {
  const steps = [
    {
      id: 1,
      title: "Procurement Agent",
      side: "left",
      points: [
        "Optimises purchase planning",
        "Prevents over-buying and stock-outs",
        "Gives actionable insights based on supplier performance & lead time",
      ],
    },
    {
      id: 2,
      title: "Operations Agent",
      side: "right",
      points: [
        "Analyses production flow",
        "Highlights WIP delays and material movement issues",
        "Improves overall plant efficiency",
      ],
    },
    {
      id: 3,
      title: "POGGY Agent (Constraint Detection)",
      side: "left",
      points: [
        "Identifies the bottleneck using TOC principles",
        "Pinpoints the single constraint limiting production or availability",
        "Shows impact on working capital and dispatch",
      ],
    },
    {
      id: 4,
      title: "Location Agent",
      side: "right",
      points: [
        "Tracks inventory across stores, warehouses & racks",
        "Identifies mismatch, wrong locations & pilferage indicators",
      ],
    },
    {
      id: 5,
      title: "Trends Agent",
      side: "left",
      points: [
        "Tracks consumption patterns",
        "Shows seasonality, weekly-monthly trends",
        "Strengthens AI-based demand forecasting",
      ],
    },
  ];

  return (
    <div className="bg-[#fdfaff] min-h-screen overflow-x-hidden">
      <Header />

      <section className="pt-40 pb-24 px-4 font-[Poppins]">
        {/* SECTION TITLE */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
            5 AI Agents used in <span className="text-pink-600">Adeya</span>
          </h1>
          <div className="h-1.5 w-24 bg-pink-500 mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* ====== SVG CURVE: Refined & Elegant ====== */}
          <svg
            className="absolute left-1/2 -translate-x-1/2 top-5 h-[calc(100%-60px)] w-[160px] lg:w-[180px] hidden md:block pointer-events-none"
            viewBox="0 0 180 1000"
            preserveAspectRatio="none"
          >
            <path
              d="M90 30 C 165 125, 180 125, 90 285 C 50 375, 0 375, 90 520 C 80 570, 180 625, 90 750 C 0 875, 0 875, 90 1000"
              fill="none"
              stroke="#f9a8d4"
              strokeWidth="4"
              strokeDasharray="10 10"
              className="opacity-60"
            />
          </svg>

          {/* ============ STEPS CONTENT ============ */}
          <div className="relative z-10 flex flex-col gap-20 md:gap-32">
            {steps.map((step) => {
              const isLeft = step.side === "left";

              return (
                <div
                  key={step.id}
                  className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr] items-center group"
                >
                  {/* LEFT CONTENT */}
                  <div
                    className={`
                      flex flex-col items-center md:items-end text-center md:text-right md:pr-10
                      ${!isLeft ? "md:invisible order-2 md:order-1" : "order-2 md:order-1"}
                    `}
                  >
                    <h3 className="font-bold text-2xl text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">
                      {step.title}
                    </h3>
                    <ul className="text-gray-500 space-y-3 text-base leading-relaxed">
                      {step.points.map((p, i) => (
                        <li key={i} className="flex flex-row-reverse items-start gap-2">
                          <span className="text-pink-500 font-bold ml-2">•</span> {p}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CENTER BUBBLE - The AI Node */}
                  <div className="flex justify-center order-1 md:order-2 mb-6 md:mb-0">
                    <div className="w-16 h-16 rounded-full bg-white border-4 border-pink-500 text-pink-600 flex items-center justify-center font-black text-2xl shadow-xl shadow-pink-100 group-hover:scale-110 transition-transform cursor-default">
                      {step.id}
                    </div>
                  </div>

                  {/* RIGHT CONTENT */}
                  <div
                    className={`
                      hidden md:flex flex-col items-start text-left pl-10 order-3
                      ${isLeft ? "invisible" : "visible"}
                    `}
                  >
                    <h3 className="font-bold text-2xl text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">
                      {step.title}
                    </h3>
                    <ul className="text-gray-500 space-y-3 text-base leading-relaxed">
                      {step.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-pink-500 font-bold mr-2">•</span> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ============ COMPARISON SECTION: LUXURY GLASS UI ============ */}
        <div className="mt-48 max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-16 text-gray-800">
            Evolution of Intelligence
          </h2>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Tally ERP Card */}
            <div className="bg-white border-2 border-gray-100 rounded-[2rem] p-10 shadow-xl shadow-gray-200/50 hover:border-gray-300 transition-all">
              <div className="text-center mb-8">
                <span className="bg-gray-100 text-gray-500 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Legacy System</span>
                <h3 className="text-2xl font-bold mt-4 text-gray-800">Tally ERP</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Shows what happened (Data)",
                  "Captures transactions",
                  "Reports what happened",
                  "No TOC Intelligence",
                  "Static inventory planning",
                  "Needs manual tracking"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-600 text-sm">
                    <div className="w-5 h-5 rounded-full border border-gray-200 flex items-center justify-center text-[10px]">✕</div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Adeya AI Card */}
            <div className="bg-white border-2 border-pink-500 rounded-[2rem] p-10 shadow-2xl shadow-pink-200/50 relative overflow-hidden group">
              {/* Highlight Flare */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-pink-100 rounded-full blur-3xl opacity-50"></div>
              
              <div className="text-center mb-8">
                <span className="bg-pink-600 text-white px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Agentic AI</span>
                <h3 className="text-2xl font-bold mt-4 text-gray-900">Adeya AI</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Shows direction (Insights)",
                  "Identifies constraints",
                  "Predicts what will happen",
                  "TOC-powered reasoning",
                  "5 Agents monitor everything",
                  "Real-time adaptive planning"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-800 text-sm font-semibold">
                    <div className="w-5 h-5 bg-pink-500 text-white rounded-full flex items-center justify-center text-[10px]">✓</div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}